import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Canvas } from '@react-three/fiber';
import { BakeShadows, PerformanceMonitor, Preload } from '@react-three/drei';
import { useRouter } from 'next/router';

// custom 3D objects
import TeamTV from '3D/models/TeamTV';
import LightingRig from '3D/scenes/Team/objects/LightingRig';
import { TeamMemberContext, useTeamMemberContext, TeamMember, GlitchContext } from 'context/Team';

interface ICanvas {
  $innerHeight?: number;
}

const StyledCanvas = styled(Canvas)<ICanvas>`
  position: absolute !important;
  pointer-events: none !important;
  top: 0;
  left: 0;
  z-index: 1;
  ${({ theme }) => theme.mq.lg`
    height: 100vh !important;
  `}
`;

export interface ITeamScene {
  teamMembers: {
    teamMemberName: string;
    teamMemberPosition: string;
  }[];
}

const TeamScene: React.FC<ITeamScene> = ({ teamMembers }: ITeamScene) => {
  const { config, setContext } = useTeamMemberContext();
  const [dpr, setDpr] = useState(2);
  const [onClient, setOnClient] = useState<boolean>(false);
  const { pathname } = useRouter();

  const handleWindowResize = useCallback(() => {
    setContext({ innerHeight: window.innerHeight });
  }, [setContext]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize, false);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [handleWindowResize]);

  useEffect(() => {
    // format and push teamMembers into context
    const formattedTeamMembers: TeamMember[] = [];
    // this removes the annoying typename prop
    for (const teamMember of teamMembers) {
      const { teamMemberPosition, teamMemberName } = teamMember || {};
      formattedTeamMembers.push({ name: teamMemberName, position: teamMemberPosition });
    }
    formattedTeamMembers.sort((a: TeamMember, b: TeamMember) => a!.name.localeCompare(b!.name));
    setContext({ teamMembers: formattedTeamMembers, innerHeight: window.innerHeight });
  }, []);

  useEffect(() => {
    if (pathname) setOnClient(true);
  }, [pathname]);

  return (
    <TeamMemberContext.Consumer>
      {value => (
        <GlitchContext.Consumer>
          {glitchVal => (
            <StyledCanvas dpr={dpr} $innerHeight={config.innerHeight}>
              <TeamMemberContext.Provider value={value}>
                <GlitchContext.Provider value={glitchVal}>
                  {onClient && (
                    <PerformanceMonitor onDecline={() => setDpr(1)} onIncline={() => setDpr(1.5)}>
                      {/* lights */}
                      <LightingRig />
                      {/* scene objects */}
                      <TeamTV />
                      {/* bake shadows into scene - makes scene more performant - needs to be placed AFTER all models in scene */}
                      <BakeShadows />
                      {/* preloads the meshes */}
                      <Preload all />
                    </PerformanceMonitor>
                  )}
                </GlitchContext.Provider>
              </TeamMemberContext.Provider>
            </StyledCanvas>
          )}
        </GlitchContext.Consumer>
      )}
    </TeamMemberContext.Consumer>
  );
};

export default TeamScene;
